import { useState, useEffect, useCallback, useRef, useMemo, Suspense } from "react";
import { lazyWithRetry } from "../lib/lazyWithRetry.js";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronUp, Pencil, Trash2 } from "lucide-react";

import { api, getAccessToken, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useAuth } from "../hooks/useAuth.js";
import { useLookup } from "../context/LookupContext.js";
import { useToast } from "../context/ToastContext.js";
import { useConfirm } from "../components/ui/confirm-dialog.js";
import { usePageBack } from "../context/PageBackContext.js";
import { cn } from "../lib/utils.js";
import { fmtDate } from "../lib/compensation.js";
import { DropdownButton, DropdownCheckboxRow, DropdownPanel, DropdownSelect, useCloseOnOutsideClick } from "../components/DropdownButton.js";
import { MenuList } from "../components/ui/menu-list.js";
import { Pagination } from "../components/ui/pagination.js";
import { SpecularButton } from "../components/ui/specular-button.js";
import { Button } from "../components/ui/button.js";
import { StatusFilterChips } from "./employees/StatusFilterChips.js";
import { EmployeeSummarySkeleton } from "./employees/EmployeeSummarySkeleton.js";
import { employeeStatsQuery, employeeSummaryModel, type EmployeeStats } from "./employees/employeeSummary.js";

// Recharts is ~600 kB; the summary strip is HR-only and collapsible, so it loads
// on demand rather than in the entry chunk.
const EmployeeSummaryCharts = lazyWithRetry(() => import("./employees/EmployeeSummaryCharts.js"));
import { HR_ROLES } from "@hrms/types";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Department { id: string; name: string; code: string; office?: { id: string; name: string } }
interface Office { id: string; name: string; city: string; country: string }
interface Manager { id: string; firstName: string; lastName: string; employeeCode: string }

interface Employee {
  id: string;
  employeeCode: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  email: string;
  designation: string;
  employeeType: string;
  status: string;
  workLocation: string;
  role: string;
  currency: string;
  baseSalary?: string | null;
  currentCtc?: string | null;
  hireDate: string;
  contractEndDate?: string | null;
  department: Department | null;
  office: Office | null;
  manager: Manager | null;
  sourceSystem?: string | null;
}

function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

interface OrgMeta { offices: Office[]; departments: Department[] }
interface ListMeta { total: number; page: number; limit: number; totalPages: number }

// ─── Constants ───────────────────────────────────────────────────────────────

const EMPLOYEE_TYPES = ["FULL_TIME", "PART_TIME", "CONTRACTOR", "INTERN"];

/** How long the Summary stays open on arrival before collapsing itself. */
const SUMMARY_PEEK_MS = 2500;
// Module scope, so both survive this page unmounting: the reveal plays once per
// app load, not every time someone comes back from an employee's detail page,
// and the Summary returns in whatever state they left it in.
let summaryRevealed = false;
let summaryOpen = true;

const STATUSES = ["ONBOARDING", "ACTIVE", "ON_LEAVE", "NOTICE_PERIOD", "TERMINATED", "CONTRACTOR", "INTERN"];

const SORT_COLUMNS: Array<{ key: string; label: string }> = [
  { key: "firstName", label: "Employee" },
  { key: "employeeCode", label: "Code" },
  { key: "designation", label: "Designation" },
  { key: "status", label: "Status" },
  { key: "hireDate", label: "Hire Date" },
];

const ALL_COLUMNS = ["Employee", "Code", "Designation", "Department", "Office", "Type", "Hire Date", "Status"];
// Always visible: shown checked but can't be unticked in the Columns menu.
const LOCKED_COLUMNS = new Set(["Employee", "Code"]);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function decodeRole(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!));
    return (payload as Record<string, unknown>)["role"] as string ?? "";
  } catch { return ""; }
}

function decodeEmployeeId(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!));
    return (payload as Record<string, unknown>)["employeeId"] as string ?? "";
  } catch { return ""; }
}

function getUserId(): string {
  try {
    const token = getAccessToken() ?? "";
    return String((JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>)["sub"] ?? "");
  } catch { return ""; }
}

function loadPrefs(key: string): Record<string, unknown> {
  try { return JSON.parse(localStorage.getItem(key) ?? "{}") as Record<string, unknown>; }
  catch { return {}; }
}

function statusColor(status: string): { background: string; color: string } {
  switch (status) {
    case "ACTIVE":         return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
    case "ON_LEAVE":       return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
    case "NOTICE_PERIOD":  return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
    case "TERMINATED":     return { background: "var(--color-surface-100)", color: "var(--color-text-secondary)" };
    default:               return { background: "var(--color-info-bg-subtle)", color: "var(--color-info-fg)" };
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const style = statusColor(status);
  return (
    <span style={{
      ...style, padding: "2px 8px", borderRadius: "var(--radius-full)",
      fontSize: "11px", fontWeight: 600, letterSpacing: "0.4px", whiteSpace: "nowrap",
    }}>
      {status.replace("_", " ")}
    </span>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px",
  background: "var(--color-surface-0)", outline: "none",
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function EmployeesPage() {
  usePageBack("/dashboard");
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const userRole   = accessToken ? decodeRole(accessToken) : "";
  const isHR       = HR_ROLES.has(userRole);
  const viewerEmployeeId = accessToken ? decodeEmployeeId(accessToken) : "";
  // Only the tiers that may act on a team member open their profile; a team lead sees
  // the roster but not the person's record.
  const isManager  = userRole === "MANAGER" || userRole === "SR_MANAGER";
  /**
   * Who can open a row as a view-only profile. HR is excluded because it has the
   * Edit action column instead.
   *
   * A manager may open anyone in this list: the list itself is already scoped
   * server-side to their reporting chain (getVisibleEmployeeScope), so every row they
   * can see is one of their own people -- there is no second check to make here.
   * The profile opens read-only apart from Work Schedule, which stays editable for a
   * manager so they can set their team's shifts.
   */
  const canOpenProfile = (empId: string) => !isHR && !!viewerEmployeeId && (isManager || empId === viewerEmployeeId);
  const workLocations = useLookup("WORK_LOCATION");
  void workLocations;

  const PREFS_KEY = useMemo(() => `hrms_prefs_employees_${getUserId()}`, []);

  // List state
  const [employees, setEmployees]   = useState<Employee[]>([]);
  const [meta, setMeta]             = useState<ListMeta>({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");

  // Stats
  const [stats, setStats] = useState<EmployeeStats | null>(null);

  // Filters — persisted to localStorage
  const [search, setSearch]         = useState("");
  const [filterStatus, setFilterStatus] = useState(() => String(loadPrefs(PREFS_KEY).filterStatus ?? ""));
  const [filterDept, setFilterDept]     = useState(() => String(loadPrefs(PREFS_KEY).filterDept ?? ""));
  const [filterOffice, setFilterOffice] = useState(() => String(loadPrefs(PREFS_KEY).filterOffice ?? ""));
  const [filterType, setFilterType]     = useState(() => String(loadPrefs(PREFS_KEY).filterType ?? ""));
  const [page, setPage]             = useState(1);
  const [pageSize, setPageSize]     = useState(20);

  // Sort — persisted to localStorage
  const [sortBy, setSortBy]   = useState(() => String(loadPrefs(PREFS_KEY).sortBy ?? ""));
  const [sortDir, setSortDir] = useState<"asc" | "desc">(() => {
    const d = loadPrefs(PREFS_KEY).sortDir;
    return d === "asc" || d === "desc" ? d : "asc";
  });

  // Column visibility — persisted to localStorage
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => {
    const raw = loadPrefs(PREFS_KEY).hiddenCols;
    return new Set(Array.isArray(raw) ? (raw as string[]).filter(c => !LOCKED_COLUMNS.has(c)) : []);
  });
  // Which single dropdown/popover is open across the filter bar — opening one closes any other.
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const colPopRef = useRef<HTMLDivElement>(null);
  useCloseOnOutsideClick(colPopRef, openFilter === "columns", () => setOpenFilter(f => f === "columns" ? null : f));
  // First arrival opens the Summary so it can reveal itself and collapse (see
  // the peek effect); after that this restores the state it was left in.
  const [showStats, setShowStats] = useState(summaryOpen);
  const summaryToggledRef = useRef(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();

  // Org meta (for filter dropdowns)
  const [orgMeta, setOrgMeta] = useState<OrgMeta>({ offices: [], departments: [] });

  // Narrow the department filter's options to the selected office, if any.
  const visibleDepartments = useMemo(
    () => orgMeta.departments.filter((d) => !filterOffice || d.office?.id === filterOffice),
    [orgMeta.departments, filterOffice]
  );

  // Departments are now per-office, so the same name (e.g. "Wireline")
  // can legitimately appear more than once — used to disambiguate those.
  const deptNameCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of visibleDepartments) counts[d.name] = (counts[d.name] ?? 0) + 1;
    return counts;
  }, [visibleDepartments]);

  const isSuperAdmin = userRole === "SUPER_HR_ADMIN";
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fullName = (emp: Employee) => [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ");

  async function deleteEmployee(emp: Employee) {
    setDeletingId(emp.id);
    try {
      await api.delete(`/employees/${emp.id}`);
      void fetchEmployees(page, search);
      loadStats();
      toast.show(`${fullName(emp)} deleted.`, "success");
    } catch (err) {
      // The confirmation card is gone by the time this lands, so the failure
      // is reported where the user is actually looking.
      logError("EmployeesPage.deleteEmployee", err);
      toast.show(errMsg(err) ?? "Could not delete this employee.", "error");
    } finally {
      setDeletingId(null);
    }
  }

  // Search debounce
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchEmployees = useCallback(async (p: number, q: string) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(pageSize) });
      if (q)             params.set("search",       q);
      if (filterStatus)  params.set("status",       filterStatus);
      if (filterDept)    params.set("departmentId", filterDept);
      if (filterOffice)  params.set("officeId",     filterOffice);
      if (filterType)    params.set("employeeType", filterType);
      if (sortBy)        { params.set("sortBy", sortBy); params.set("sortDir", sortDir); }
      const { data } = await api.get<{ data: Employee[]; meta: ListMeta }>(`/employees?${params}`);
      setEmployees(data.data);
      setMeta(data.meta);
    } catch (err) {
      logError("EmployeesPage.fetchEmployees", err);
      setError("Failed to load employees.");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterDept, filterOffice, filterType, sortBy, sortDir, pageSize]);

  useEffect(() => {
    fetchEmployees(page, search);
  }, [page, filterStatus, filterDept, filterOffice, filterType, sortBy, sortDir, pageSize, fetchEmployees]);

  // If the selected office changes (or loads from persisted prefs) and the
  // currently-selected department doesn't belong to it, reset the department
  // filter rather than silently sending a mismatched, zero-result query.
  useEffect(() => {
    if (!filterDept || !filterOffice) return;
    const dept = orgMeta.departments.find((d) => d.id === filterDept);
    if (dept && dept.office?.id !== filterOffice) { setFilterDept(""); setPage(1); }
  }, [filterOffice, orgMeta.departments]);

  // The Summary narrows with the table: same filters, same query string.
  const loadStats = useCallback(() => {
    if (!isHR) return;
    const qs = employeeStatsQuery({
      search, status: filterStatus, departmentId: filterDept,
      officeId: filterOffice, employeeType: filterType,
    });
    api.get<EmployeeStats>(`/employees/stats${qs}`)
      .then(({ data }) => setStats(data))
      .catch((err) => logError("EmployeesPage.loadStats", err));
  }, [isHR, search, filterStatus, filterDept, filterOffice, filterType]);

  useEffect(() => {
    api.get<OrgMeta>("/employees/org-meta").then(({ data }) => setOrgMeta(data)).catch((err) => logError("EmployeesPage.loadOrgMeta", err));
  }, []);

  // Debounced so typing in the search box doesn't fire a stats query per keystroke.
  useEffect(() => {
    const timer = window.setTimeout(loadStats, 300);
    return () => clearTimeout(timer);
  }, [loadStats]);

  // Remembered for the next mount, so returning to the list doesn't reopen a
  // Summary the user had closed (or close one they had opened).
  useEffect(() => { summaryOpen = showStats; }, [showStats]);

  // Collapse the Summary once the reveal has been seen -- once per app load.
  // The timer only starts when the stats have actually arrived, so a slow
  // response still gets its full moment on screen, and touching the toggle
  // cancels it for good.
  useEffect(() => {
    if (!isHR || !stats || summaryRevealed || summaryToggledRef.current) return;
    const timer = window.setTimeout(() => {
      summaryRevealed = true;
      if (!summaryToggledRef.current) setShowStats(false);
    }, SUMMARY_PEEK_MS);
    return () => clearTimeout(timer);
  }, [isHR, stats]);

  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({
        filterStatus, filterDept, filterOffice, filterType,
        sortBy, sortDir, hiddenCols: Array.from(hiddenCols),
      }));
    } catch { /* private browsing */ }
  }, [PREFS_KEY, filterStatus, filterDept, filterOffice, filterType, sortBy, sortDir, hiddenCols]);

  function resetFilters() {
    try { localStorage.removeItem(PREFS_KEY); } catch { /* */ }
    setFilterStatus(""); setFilterDept(""); setFilterOffice(""); setFilterType("");
    setSortBy(""); setSortDir("asc"); setHiddenCols(new Set());
    setPage(1);
  }

  function onSearchChange(val: string) {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchEmployees(1, val);
    }, 300);
  }

  function toggleSort(field: string) {
    if (sortBy === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
    setPage(1);
  }

  function toggleCol(col: string) {
    if (LOCKED_COLUMNS.has(col)) return;
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col); else next.add(col);
      return next;
    });
  }

  function sortArrow(field: string) {
    if (sortBy !== field) return <span style={{ opacity: 0.3, marginLeft: 3 }}>↕</span>;
    return <span style={{ marginLeft: 3 }}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  }

  const { totalHeadcount, statusFilters, statusMix } = employeeSummaryModel(stats);

  return (
    <div style={{ minHeight: "100vh", background: "var(--color-surface-50)" }}>
      {/* Page header */}
      <div style={{
        background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)",
        padding: "var(--space-3) var(--space-8)", display: "flex", alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div>
          <h1 style={{ fontSize: "18px", fontWeight: 700, color: "var(--color-surface-900)" }}>Employees</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isHR && (
            <button onClick={() => navigate("/employees/new")} style={{
              background: "var(--color-brand-600)", color: "#fff", border: "none",
              padding: "8px 16px", borderRadius: "var(--radius-md)", fontSize: "14px",
              fontWeight: 600, cursor: "pointer",
            }}>
              + Add Employee
            </button>
          )}
        </div>
      </div>

      {/* Overview toggle — mirrors the Summary control on Attendance → Weekly Hours */}
      {isHR && (
        <div style={{
          background: "var(--color-surface-0)",
          borderBottom: showStats ? "none" : "1px solid var(--color-surface-200)",
          padding: "var(--space-2) var(--space-8)",
          display: "flex", justifyContent: "flex-end",
        }}>
          <button
            onClick={() => { summaryToggledRef.current = true; summaryRevealed = true; setShowStats(s => !s); }}
            className="flex items-center gap-1.5 text-xs font-semibold text-surface-700 hover:text-surface-900"
          >
            {showStats ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />} Summary
          </button>
        </div>
      )}

      {/* Stats strip — always mounted, so opening and closing can animate.
          The grid-rows 0fr/1fr pair collapses it to the exact height of its
          own content without measuring anything. */}
      {isHR && (
        <div
          aria-hidden={!showStats}
          className={cn(
            "grid transition-[grid-template-rows,opacity] duration-500 ease-out motion-reduce:transition-none",
            showStats ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          )}
        >
          <div className="overflow-hidden">
            <div style={{ background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)", padding: "var(--space-4) var(--space-8)" }}>
              {/* Until the first stats response lands there is nothing to draw, so
                  the skeleton holds the exact layout and the charts then animate
                  up from zero into it. Later filter changes keep the current
                  charts on screen rather than flashing back to the skeleton. */}
              {stats ? (
                <>
                  {/* Status filter — replaces the old count tiles. Each chip carries
                      its own headcount, and selecting one narrows both the table and
                      the charts below. */}
                  <StatusFilterChips
                    options={statusFilters}
                    value={filterStatus}
                    onChange={v => { setFilterStatus(v); setPage(1); }}
                    className="mb-4"
                  />

                  {/* Charts — each one filters the table it sits above */}
                  <Suspense fallback={<EmployeeSummarySkeleton chips={false} />}>
                    <EmployeeSummaryCharts
                      byDept={stats.byDept ?? []}
                      byOffice={stats.byOffice ?? []}
                      statusMix={statusMix}
                      totalHeadcount={totalHeadcount}
                      filterDept={filterDept}
                      filterOffice={filterOffice}
                      filterStatus={filterStatus}
                      onSelectDept={id => { setFilterDept(id ?? ""); setPage(1); }}
                      onSelectOffice={id => { setFilterOffice(id ?? ""); setPage(1); }}
                      onSelectStatus={id => { setFilterStatus(id ?? ""); setPage(1); }}
                    />
                  </Suspense>
                </>
              ) : (
                <EmployeeSummarySkeleton />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      {isHR && (
        <div style={{
          background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)",
          padding: "var(--space-4) var(--space-8)", display: "flex", gap: "var(--space-3)",
          flexWrap: "nowrap", alignItems: "center",
        }}>
          <input
            placeholder="Search name, email, code…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{ ...inputStyle, width: (searchFocused || search) ? "260px" : "150px", transition: "width 0.15s ease", flexShrink: 0 }}
          />
          <DropdownSelect
            value={filterStatus}
            onChange={(v) => { setFilterStatus(v); setPage(1); }}
            placeholder="All Statuses"
            options={STATUSES.map((s) => ({ value: s, label: s.replace("_", " ") }))}
            open={openFilter === "status"}
            onToggle={() => setOpenFilter(f => f === "status" ? null : "status")}
            onClose={() => setOpenFilter(f => f === "status" ? null : f)}
          />
          <DropdownSelect
            value={filterDept}
            onChange={(v) => { setFilterDept(v); setPage(1); }}
            placeholder="All Departments"
            options={visibleDepartments.map((d) => ({
              value: d.id,
              label: (deptNameCounts[d.name] ?? 0) > 1 && d.office ? `${d.name} — ${d.office.name}` : d.name,
            }))}
            open={openFilter === "dept"}
            onToggle={() => setOpenFilter(f => f === "dept" ? null : "dept")}
            onClose={() => setOpenFilter(f => f === "dept" ? null : f)}
          />
          <DropdownSelect
            value={filterOffice}
            onChange={(v) => { setFilterOffice(v); setPage(1); }}
            placeholder="All Offices"
            options={orgMeta.offices.map((o) => ({ value: o.id, label: o.name }))}
            open={openFilter === "office"}
            onToggle={() => setOpenFilter(f => f === "office" ? null : "office")}
            onClose={() => setOpenFilter(f => f === "office" ? null : f)}
          />
          <DropdownSelect
            value={filterType}
            onChange={(v) => { setFilterType(v); setPage(1); }}
            placeholder="All Types"
            options={EMPLOYEE_TYPES.map((t) => ({ value: t, label: t.replace("_", " ") }))}
            open={openFilter === "type"}
            onToggle={() => setOpenFilter(f => f === "type" ? null : "type")}
            onClose={() => setOpenFilter(f => f === "type" ? null : f)}
          />

          {/* Column picker */}
          <div ref={colPopRef} className="relative shrink-0">
            <DropdownButton expanded={openFilter === "columns"} onClick={() => setOpenFilter(f => f === "columns" ? null : "columns")}>
              Columns
            </DropdownButton>
            {openFilter === "columns" && (
              <DropdownPanel className="min-w-[160px]">
                <MenuList className="p-2">
                  {ALL_COLUMNS.map(col => (
                    <DropdownCheckboxRow key={col} checked={!hiddenCols.has(col)} disabled={LOCKED_COLUMNS.has(col)} onChange={() => toggleCol(col)}>
                      {col}
                    </DropdownCheckboxRow>
                  ))}
                </MenuList>
              </DropdownPanel>
            )}
          </div>

          {(() => {
            const hasActiveFilters = !!(filterStatus || filterDept || filterOffice || filterType || search || sortBy || hiddenCols.size > 0);
            return (
              <SpecularButton
                size="lg"
                onClick={() => { resetFilters(); setSearch(""); }}
                disabled={!hasActiveFilters}
                className="h-9 px-2.5 font-normal text-surface-900"
              >
                Reset
              </SpecularButton>
            );
          })()}
        </div>
      )}

      {/* Table */}
      <div style={{ padding: "calc(var(--space-6) * 0.4) calc(var(--space-8) * 0.4)" }}>
        {error && (
          <div style={{
            background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "var(--space-4)",
            borderRadius: "var(--radius-md)", marginBottom: "var(--space-4)",
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "var(--space-12)", color: "var(--color-surface-700)" }}>
            Loading…
          </div>
        ) : employees.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "var(--space-12)", color: "var(--color-surface-700)",
            background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)",
            border: "1px solid var(--color-surface-200)",
          }}>
            No employees found.
          </div>
        ) : (
          <>
            <div style={{
              background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)",
              border: "1px solid var(--color-surface-200)", overflow: "hidden",
            }}>
              <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                    {[
                      { key: "Employee", label: "Employee", sortKey: "firstName" },
                      { key: "Code", label: "Code", sortKey: "employeeCode" },
                      { key: "Designation", label: "Designation", sortKey: "designation" },
                      { key: "Department", label: "Department", sortKey: "department" },
                      { key: "Office", label: "Office", sortKey: "office" },
                      { key: "Type", label: "Type", sortKey: "employeeType" },
                      { key: "HireDate", label: "Hire Date", sortKey: "hireDate" },
                      { key: "Status", label: "Status", sortKey: "status" },
                      ...(isHR ? [{ key: "Actions", label: "Actions", sortKey: "" }] : []),
                    ].filter(h => h.label === "" || !hiddenCols.has(h.label)).map(h => (
                      <th key={h.key} onClick={h.sortKey ? () => toggleSort(h.sortKey) : undefined}
                        style={{
                          padding: "10px 14px", textAlign: "left", fontSize: "11px",
                          fontWeight: 700, color: "var(--color-surface-700)", textTransform: "uppercase",
                          letterSpacing: "0.5px", whiteSpace: "nowrap",
                          cursor: h.sortKey ? "pointer" : "default",
                          userSelect: "none",
                        }}>
                        {h.label}
                        {h.sortKey && sortArrow(h.sortKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp, i) => (
                    <tr
                      key={emp.id}
                      className={cn(
                        "transition-colors duration-200 ease-out hover:bg-surface-100 motion-reduce:transition-none",
                        canOpenProfile(emp.id) && "cursor-pointer focus-visible:bg-surface-100 focus-visible:outline-none",
                      )}
                      {...(canOpenProfile(emp.id) && {
                        role: "link",
                        tabIndex: 0,
                        title: emp.id === viewerEmployeeId ? "View your profile" : "View profile and set shift",
                        onClick: () => navigate(`/employees/${emp.id}/view`),
                        onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === "Enter") navigate(`/employees/${emp.id}/view`);
                        },
                      })}
                      style={{
                        borderBottom: i < employees.length - 1 ? "1px solid var(--color-surface-100)" : "none",
                      }}
                    >
                      {!hiddenCols.has("Employee") && (
                        <td style={{ padding: "12px 14px", maxWidth: 220 }}>
                          <div style={{ fontWeight: 600, color: "var(--color-surface-900)", display: "flex", alignItems: "center", gap: 6 }}>
                            <span title={[emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ")} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {[emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ")}
                            </span>
                            {emp.sourceSystem === "ADP" && (
                              <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: "var(--color-info-bg)", color: "var(--color-info-fg)", flexShrink: 0 }}>ADP</span>
                            )}
                          </div>
                          <div title={emp.email} style={{ fontSize: "12px", color: "var(--color-surface-700)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{emp.email}</div>
                        </td>
                      )}
                      {!hiddenCols.has("Code") && (
                        <td style={{ padding: "12px 14px", fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--color-surface-700)", whiteSpace: "nowrap" }}>
                          {emp.employeeCode}
                        </td>
                      )}
                      {!hiddenCols.has("Designation") && (
                        <td title={emp.designation} style={{ padding: "12px 14px", fontSize: "13px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{emp.designation}</td>
                      )}
                      {!hiddenCols.has("Department") && (
                        <td title={emp.department?.name ?? "—"} style={{ padding: "12px 14px", fontSize: "13px", color: "var(--color-surface-700)", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {emp.department?.name ?? "—"}
                        </td>
                      )}
                      {!hiddenCols.has("Office") && (
                        <td style={{ padding: "12px 14px", fontSize: "13px", color: "var(--color-surface-700)", whiteSpace: "nowrap" }}>
                          {emp.office?.city ?? "—"}
                        </td>
                      )}
                      {!hiddenCols.has("Type") && (
                        <td style={{ padding: "12px 14px", fontSize: "12px", color: "var(--color-surface-700)", whiteSpace: "nowrap" }}>
                          {emp.employeeType.replace("_", " ")}
                        </td>
                      )}
                      {!hiddenCols.has("Hire Date") && (
                        <td className="whitespace-nowrap px-3.5 py-3 text-[13px] text-surface-700">
                          {emp.hireDate ? fmtDate(emp.hireDate) : "—"}
                        </td>
                      )}
                      {!hiddenCols.has("Status") && (
                        <td style={{ padding: "12px 14px" }}>
                          <StatusBadge status={emp.status} />
                          {(() => {
                            const days = daysUntil(emp.contractEndDate);
                            if (days === null) return null;
                            if (days < 0) return <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: "var(--color-error-bg)", color: "var(--color-error-fg)" }}>Contract ended</span>;
                            if (days <= 30) return <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: "var(--color-warning-bg)", color: "var(--color-warning-fg)", display: "inline-flex", alignItems: "center", gap: 3 }}><AlertTriangle size={10} /> {days}d left</span>;
                            return null;
                          })()}
                        </td>
                      )}
                      {isHR && (
                        <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="bg-surface-100 text-surface-700 hover:bg-surface-200"
                              aria-label={`Edit ${fullName(emp)}`}
                              title="Edit"
                              onClick={() => navigate(`/employees/${emp.id}/edit`)}
                            >
                              <Pencil className="size-3.5" />
                            </Button>
                            {isSuperAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="bg-error/10 text-error hover:bg-error/20"
                                aria-label={`Delete ${fullName(emp)}`}
                                title="Delete"
                                disabled={deletingId === emp.id}
                                onClick={() => void confirm({
                                  title: "Delete this employee?",
                                  description: `Permanently removes ${fullName(emp)} (${emp.employeeCode}) and all of their records. This cannot be undone.`,
                                  actionText: "Delete permanently",
                                  tone: "destructive",
                                }).then(ok => { if (ok) void deleteEmployee(emp); })}
                              >
                                <Trash2 className="size-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>

            {/* Pagination */}
            <Pagination
              meta={meta}
              onPageChange={setPage}
              onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
              pageSizeOptions={[10, 20, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );
}
